import React, { useMemo } from 'react';
import { Polyline, Marker } from '@react-google-maps/api';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { trackDataSelector } from '../../../features/datacenter/historySlice';
import { gensetFocusAssetIdSelector, gensetTrackReportIdSelector } from '../../../features/map/mapSlice';
import { selectGensetMergedVehicles } from '../../../features/genset/gensetSlice';

const buildRunningRangesUtc = (periods = []) => {
    return (Array.isArray(periods) ? periods : []).map((period) => {
        const start = period?.startTime ? moment(period.startTime, 'YYYY-MM-DD HH:mm:ss').subtract(5, 'hours').subtract(30, 'minutes') : null;
        const end = period?.endTime
            ? moment(period.endTime, 'YYYY-MM-DD HH:mm:ss').subtract(5, 'hours').subtract(30, 'minutes')
            : moment().subtract(5, 'hours').subtract(30, 'minutes');
        if (!start || !end || !start.isValid() || !end.isValid()) return null;
        return { start: start.valueOf(), end: end.valueOf() };
    }).filter(Boolean);
};

const buildRunningSegments = (trackPoints = [], ranges = []) => {
    if (!ranges.length || !trackPoints.length) return [];
    const segments = [];
    let current = [];
    trackPoints.forEach((point) => {
        const stamp = moment.utc(point.dt_tracker, 'YYYY-MM-DD HH:mm:ss', true).valueOf();
        const inRange = ranges.some((range) => stamp >= range.start && stamp <= range.end);
        if (inRange) {
            current.push({ lat: point.lat, lng: point.lng });
        } else if (current.length) {
            segments.push(current);
            current = [];
        }
    });
    if (current.length) segments.push(current);
    return segments;
};

const buildRunningMarkers = (trackPoints = [], ranges = []) => {
    if (!ranges.length || !trackPoints.length) return [];
    const stampIndexPairs = trackPoints
        .map((point, index) => {
            const stamp = moment.utc(point.dt_tracker, 'YYYY-MM-DD HH:mm:ss', true).valueOf();
            if (!Number.isFinite(stamp)) return null;
            return { stamp, index };
        })
        .filter(Boolean);
    if (!stampIndexPairs.length) return [];
    const stamps = stampIndexPairs.map((pair) => pair.stamp);
    const findClosestIndex = (target) => {
        if (!Number.isFinite(target)) return null;
        let lo = 0;
        let hi = stamps.length - 1;
        while (lo < hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (stamps[mid] < target) lo = mid + 1;
            else hi = mid;
        }
        let best = lo;
        if (lo > 0 && Math.abs(stamps[lo - 1] - target) <= Math.abs(stamps[lo] - target)) {
            best = lo - 1;
        }
        return stampIndexPairs[best]?.index ?? null;
    };

    const markers = [];
    const usedIndices = new Set();
    ranges.forEach((range, idx) => {
        const startIndex = findClosestIndex(range.start);
        const endIndex = findClosestIndex(range.end);
        [startIndex, endIndex].forEach((pointIndex, positionIndex) => {
            if (pointIndex === null || pointIndex === undefined) return;
            if (usedIndices.has(pointIndex)) return;
            const point = trackPoints[pointIndex];
            if (!point) return;
            usedIndices.add(pointIndex);
            markers.push({
                lat: point.lat,
                lng: point.lng,
                key: `genset-running-marker-${idx}-${positionIndex}-${pointIndex}`,
            });
        });
    });
    return markers;
};

const GoogleGensetTrackLine = () => {
    const reportId = useSelector(gensetTrackReportIdSelector);
    const focusAssetId = useSelector(gensetFocusAssetIdSelector);
    const trackData = useSelector(trackDataSelector);
    const gensetVehicles = useSelector(selectGensetMergedVehicles);

    const runningCircleUrl = useMemo(() => {
        const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="9" cy="9" r="7" fill="none" stroke="#ef4444" stroke-width="2"/><circle cx="9" cy="9" r="4" fill="#ef4444" stroke="#b91c1c" stroke-width="1"/></svg>';
        return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    }, []);
    const runningCircleIcon = useMemo(() => {
        if (typeof window !== 'undefined' && window.google?.maps) {
            return {
                url: runningCircleUrl,
                scaledSize: new window.google.maps.Size(18, 18),
                anchor: new window.google.maps.Point(9, 9),
            };
        }
        return { url: runningCircleUrl };
    }, [runningCircleUrl]);
    const arrowIcons = useMemo(() => {
        if (typeof window === 'undefined' || !window.google?.maps) return [];
        const arrowSymbol = {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 1.5,
            strokeColor: '#073fb8',
            strokeOpacity: 0.9,
            fillColor: '#073fb8',
            fillOpacity: 0.9,
        };
        const offsets = ['15%', '32%', '50%', '68%', '85%'];
        return offsets.map((offset) => ({ icon: arrowSymbol, offset }));
    }, []);

    const { trackPath, runningSegments, runningMarkers, stops } = useMemo(() => {
        if (!reportId || !trackData?.[reportId]?.track) return { trackPath: [], runningSegments: [], runningMarkers: [] };
        const track = trackData[reportId].track;
        const trackPath = track.map((point) => ({ lat: point.lat, lng: point.lng }));
        const stops = Array.isArray(trackData?.[reportId]?.stops) ? trackData[reportId].stops : [];

        const focused = gensetVehicles.find((vehicle) => {
            const id = String(vehicle?.imei || vehicle?.IMEI || vehicle?.assetId || '').trim();
            return id && id === focusAssetId;
        });
        const runningRanges = buildRunningRangesUtc(focused?.period?.runningPeriods || []);
        const runningSegments = buildRunningSegments(track, runningRanges);
        const runningMarkers = buildRunningMarkers(track, runningRanges);
        return { trackPath, runningSegments, runningMarkers, stops };
    }, [focusAssetId, gensetVehicles, reportId, trackData]);

    if (!reportId || trackPath.length === 0) return null;

    const startFlag = stops.length === 0 || stops[0]?.startIndex !== 0;
    const endFlag = stops.length === 0 || stops[stops.length - 1]?.endIndex !== trackPath.length - 1;

    return (
        <>
            {startFlag && (
                <Marker
                    icon="/assets/history/def20/mapmarkergreen.png"
                    position={trackPath[0]}
                />
            )}
            {endFlag && (
                <Marker
                    icon="/assets/history/def20/mapmarker.png"
                    position={trackPath[trackPath.length - 1]}
                />
            )}
            {stops.map((stop, index) => {
                const stopPoint = trackData?.[reportId]?.track?.[stop.startIndex];
                if (!stopPoint) return null;
                return (
                    <Marker
                        key={`genset-stop-${index}`}
                        icon="/assets/history/def20/parking.png"
                        position={{ lat: stopPoint.lat, lng: stopPoint.lng }}
                    />
                );
            })}
            {runningMarkers.map((marker) => (
                <Marker
                    key={marker.key}
                    icon={runningCircleIcon}
                    position={{ lat: marker.lat, lng: marker.lng }}
                    options={{ zIndex: 4 }}
                />
            ))}
            <Polyline
                path={trackPath}
                options={{
                    strokeColor: '#2563eb',
                    strokeOpacity: 0.85,
                    strokeWeight: 3,
                    clickable: false,
                    draggable: false,
                    editable: false,
                    geodesic: false,
                    zIndex: 2,
                    icons: arrowIcons,
                }}
            />
            {runningSegments.map((segment, index) => (
                <Polyline
                    key={`genset-running-${index}`}
                    path={segment}
                    options={{
                        strokeColor: '#ef4444',
                        strokeOpacity: 0.95,
                        strokeWeight: 4,
                        clickable: false,
                        draggable: false,
                        editable: false,
                        geodesic: false,
                        zIndex: 3,
                    }}
                />
            ))}
        </>
    );
};

export default GoogleGensetTrackLine;
